import { MainMap } from '@c/Catalog'
import cns from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { CategoriesForMap } from '@/components/Catalog/Map/CategoriesForMap'
import SentryErrorBoundary from '@/components/SentryErrorBoundary'
import type { InterestPoint } from '@/core/interface'
import { useAppSelector } from '@/core/store'

const TOP_TAB_MAX_MARKERS = 100

function compareTopLandmarkImportance(a: InterestPoint, b: InterestPoint): number {
  if (b.rank !== a.rank) return b.rank - a.rank
  if (a.step !== b.step) return a.step - b.step
  return a.id.localeCompare(b.id)
}

export const HomePage = () => {
  const { interestPoints, selectedTags, selectedTab } = useAppSelector(
    (store) => store.catalogStore,
  )
  const location = useLocation()

  const filteredPoints = useMemo(() => {
    const filtered = interestPoints.filter((point) => filterPoint(point, selectedTags, selectedTab))
    if (selectedTab !== 'top') return filtered
    return [...filtered].sort(compareTopLandmarkImportance).slice(0, TOP_TAB_MAX_MARKERS)
  }, [selectedTags, interestPoints, selectedTab])

  const [isExcursionBeingSelected, setExcursionBeingSelected] = useState(() => {
    return (
      !!location.state?.excursionId || !!location.state?.category || !!location.state?.openTours
    )
  })

  useEffect(() => {
    if (location.state?.openTours) {
      setExcursionBeingSelected(true)
    }
  }, [location.state?.openTours])

  useEffect(() => {
    const handleOpenTours = () => setExcursionBeingSelected(true)
    window.addEventListener('open-tours-tab', handleOpenTours)
    return () => window.removeEventListener('open-tours-tab', handleOpenTours)
  }, [])

  return (
    <div className={cns('home')}>
      <SentryErrorBoundary boundaryId="homepage">
        <MainMap
          markers={filteredPoints}
          isExcursionBeingSelected={isExcursionBeingSelected}
          setExcursionBeingSelected={setExcursionBeingSelected}
          initialExcursionId={location.state?.excursionId}
        />
        <CategoriesForMap
          isExcursionBeingSelected={isExcursionBeingSelected}
          setExcursionBeingSelected={setExcursionBeingSelected}
        />
      </SentryErrorBoundary>
    </div>
  )
}

function filterPoint(point: InterestPoint, selectedTags: string[], selectedTab: string): boolean {
  const isCorrectAllPinsPoint = selectedTab === 'allPins' && selectedTags.includes(point.type)
  const isCorrectTop112Point =
    selectedTab === 'top' &&
    (point.rank === 7 || point.rank === 10) &&
    selectedTags.includes(point.type)
  const isCorrectMustSeePoint =
    selectedTab === 'mustSee' && point.tags.includes('mustsee') && selectedTags.includes(point.type)
  const isCorrectOtherPoint = point.tags.includes('other')
  const matchesCategory =
    isCorrectAllPinsPoint || isCorrectTop112Point || isCorrectMustSeePoint || isCorrectOtherPoint

  // Helper function to check if a point category matches an included category
  //TODO need know what is this???
  const categoryMatches = (pointCat: string, includedCat: string) => {
    if (includedCat === 'B' && (pointCat === 'B' || pointCat === 'B-M' || pointCat === 'B-P'))
      return true
    if (includedCat === 'M' && (pointCat === 'M' || pointCat === 'B-M')) return true

    //Option 'Other' enables anything that starts with a letter (i.e. not !,10,7,5,3 and not in the ist above)
    if (
      includedCat === 'Other' &&
      ![
        'B',
        'M',
        'B-M',
        'B-P',
        'C',
        'S',
        'P',
        'S-P',
        'REST',
        'BAR',
        'CAFE',
        'ICE-CR',
        'TERR',
        'Ponte',
      ].includes(pointCat) &&
      /^[A-Z]/.test(pointCat)
    )
      return true
    return pointCat === includedCat
  }

  // Check if the point has at least one category that matches the included list
  const matchesIncluded = point.tags.some((pointCat) =>
    selectedTags.some((includedCat) => categoryMatches(pointCat, includedCat)),
  )

  // Return true if the point matches both criteria
  return matchesCategory && matchesIncluded
}
